import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

// Record yourself saying the phrase, play it back next to the original, and
// try again until it sounds right. Nothing is analysed or uploaded: the
// recording only ever exists as a blob in this tab, which is the whole privacy
// story - and why there's no speech recognition, which is poor or missing for
// most of these languages anyway.
//
// Adapted from the VoiceRecorder POC on the more-practice-controls branch.

const MAX_SECONDS = 10;

export function recordingSupported() {
  return typeof window !== "undefined" &&
    typeof window.MediaRecorder !== "undefined" &&
    !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
}

function mimeType() {
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
  return candidates.find((type) => window.MediaRecorder.isTypeSupported && window.MediaRecorder.isTypeSupported(type)) || "";
}

export default function SayIt({ canPlayOriginal, onPlayOriginal, onRecorded, phrase }) {
  const [state, setState] = useState("idle");
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [error, setError] = useState(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  const stopTracks = () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  useEffect(() => () => {
    clearTimeout(timerRef.current);
    stopTracks();
  }, []);

  useEffect(() => () => {
    if (recordingUrl) URL.revokeObjectURL(recordingUrl);
  }, [recordingUrl]);

  if (!recordingSupported()) return null;

  const start = async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const type = mimeType();
      const recorder = new window.MediaRecorder(stream, type ? { mimeType: type } : undefined);
      const chunks = [];

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) chunks.push(event.data);
      };
      recorder.onstop = () => {
        clearTimeout(timerRef.current);
        stopTracks();
        const url = URL.createObjectURL(new Blob(chunks, { type: type || undefined }));
        setRecordingUrl(url);
        setState("recorded");
        onRecorded(phrase);
        new Audio(url).play().catch(() => {});
      };

      recorderRef.current = recorder;
      recorder.start();
      setState("recording");
      timerRef.current = setTimeout(() => recorder.state === "recording" && recorder.stop(), MAX_SECONDS * 1000);
    } catch (e) {
      stopTracks();
      setError("Couldn't use the microphone. Check your browser's permission for this site.");
      setState("idle");
    }
  };

  const stop = () => {
    if (recorderRef.current && recorderRef.current.state === "recording") recorderRef.current.stop();
  };

  const playMine = () => new Promise((resolve) => {
    const audio = new Audio(recordingUrl);
    audio.onended = resolve;
    audio.onerror = resolve;
    audio.play().catch(resolve);
  });

  const playBoth = async () => {
    await onPlayOriginal(phrase);
    await playMine();
  };

  return (
    <div className="tp-sayit">
      {state === "idle" && (
        <button className="btn btn-default tp-sayit-start" onClick={start} type="button">
          🎙 Record yourself to compare
        </button>
      )}

      {state === "recording" && (
        <button className="btn btn-danger tp-sayit-stop" onClick={stop} type="button">
          <span className="tp-sayit-dot" /> Recording… tap to stop
        </button>
      )}

      {state === "recorded" && (
        <>
          <div className="tp-sayit-compare">
            {canPlayOriginal && (
              <button className="btn btn-default btn-sm" onClick={() => onPlayOriginal(phrase)} type="button">▶ Original</button>
            )}
            <button className="btn btn-default btn-sm" onClick={playMine} type="button">▶ You</button>
            {canPlayOriginal && (
              <button className="btn btn-default btn-sm" onClick={playBoth} type="button">▶ Both</button>
            )}
            {/* Straight into another take, rather than back to the record button. */}
            <button className="btn btn-default btn-sm" onClick={start} type="button">🔁 Try again</button>
          </div>
        </>
      )}

      {error && <div className="tp-sayit-error">{error}</div>}

      <div className="tp-sayit-privacy">
        🔒 Your recording stays on this device. It&apos;s never uploaded, saved or analysed.
      </div>
    </div>
  );
}

SayIt.propTypes = {
  canPlayOriginal: PropTypes.bool.isRequired,
  onPlayOriginal: PropTypes.func.isRequired,
  onRecorded: PropTypes.func.isRequired,
  phrase: PropTypes.object.isRequired,
};
